import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import focoLogo from '@/assets/foco-ai-logo.png';

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  version: string;
  children: ReactNode;
}

export function LegalPageLayout({ title, lastUpdated, version, children }: LegalPageLayoutProps) {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-card border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={focoLogo} alt="FOCO AI System" className="h-7 w-auto" />
          </Link>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Volver
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">{title}</h1>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span>Última actualización: {lastUpdated}</span>
            <span>Versión: {version}</span>
          </div>
        </div>

        <div className="prose prose-sm sm:prose max-w-none text-foreground 
          [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-foreground
          [&_h3]:text-lg [&_h3]:font-medium [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:text-foreground
          [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p]:mb-4
          [&_ul]:text-muted-foreground [&_ul]:space-y-1 [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5
          [&_ol]:text-muted-foreground [&_ol]:space-y-1 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-5
          [&_a]:text-accent [&_a]:underline
          [&_strong]:text-foreground
        ">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-card">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-wrap gap-4 justify-center text-sm text-muted-foreground">
            <Link to="/privacidad" className="hover:text-foreground transition-colors font-medium">Centro de privacidad</Link>
            <Link to="/privacy-policy" className="hover:text-foreground transition-colors">Política de Privacidad</Link>
            <Link to="/terms-and-conditions" className="hover:text-foreground transition-colors">Términos y Condiciones</Link>
            <Link to="/cookies-policy" className="hover:text-foreground transition-colors">Política de Cookies</Link>
            <Link to="/consent-information" className="hover:text-foreground transition-colors">Consentimiento</Link>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-3">
            © {new Date().getFullYear()} FOCO AI System. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
